import { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

const theme = {
  bg: "#0a0f1a",
  surface: "#111827",
  border: "#1e2a3a",
  accent: "#00e5a0",
  accentDim: "rgba(0, 229, 160, 0.12)",
  text: "#e2e8f0",
  textMuted: "#64748b",
  error: "#f87171",
  errorBg: "rgba(248, 113, 113, 0.08)",
};

export default function AuthModal({ onClose }) {
  const [mode, setMode] = useState("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [awaitingConfirmation, setAwaitingConfirmation] = useState(false);
  const { signIn, signUp, user } = useAuth();

  // Auto-close when the user confirms their email and gets signed in
  useEffect(() => {
    if (user) onClose();
  }, [user, onClose]);

  const handleSubmit = async () => {
    if (!email || !password) return;
    setLoading(true);
    setError(null);
    try {
      if (mode === "signin") {
        await signIn(email, password);
        onClose();
      } else {
        await signUp(email, password);
        setAwaitingConfirmation(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSubmit();
    if (e.key === "Escape") onClose();
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.6)",
        backdropFilter: "blur(4px)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        style={{
          background: theme.surface,
          border: `1px solid ${theme.border}`,
          borderRadius: 16,
          padding: 32,
          width: "100%",
          maxWidth: 400,
          boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
          fontFamily: "'IBM Plex Mono', monospace",
        }}
      >
        {/* Awaiting email confirmation */}
        {awaitingConfirmation ? (
          <div style={{ textAlign: "center", padding: "8px 0 4px" }}>
            <div style={{ fontSize: 28, marginBottom: 16 }}>📬</div>
            <p style={{ color: theme.text, fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
              Check your email
            </p>
            <p style={{ color: theme.textMuted, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
              We sent a confirmation link to <strong style={{ color: theme.text }}>{email}</strong>.
              Click it to finish signing up — you'll be signed in automatically.
            </p>
          </div>
        ) : (<>

        {/* Mode switcher */}
        <div style={{ display: "flex", gap: 4, marginBottom: 28 }}>
          {["signin", "signup"].map((m) => (
            <button
              key={m}
              onClick={() => { setMode(m); setError(null); }}
              style={{
                flex: 1,
                padding: "8px 0",
                background: mode === m ? theme.accentDim : "transparent",
                border: `1px solid ${mode === m ? theme.accent : theme.border}`,
                borderRadius: 8,
                color: mode === m ? theme.accent : theme.textMuted,
                fontSize: 13,
                fontFamily: "'IBM Plex Mono', monospace",
                cursor: "pointer",
              }}
            >
              {m === "signin" ? "Sign In" : "Sign Up"}
            </button>
          ))}
        </div>

        {/* Inputs */}
        {["email", "password"].map((field) => (
          <input
            key={field}
            type={field}
            placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
            value={field === "email" ? email : password}
            onChange={(e) => field === "email" ? setEmail(e.target.value) : setPassword(e.target.value)}
            onKeyDown={handleKeyDown}
            style={{
              display: "block",
              width: "100%",
              marginBottom: 12,
              padding: "12px 14px",
              background: theme.bg,
              border: `1px solid ${theme.border}`,
              borderRadius: 8,
              color: theme.text,
              fontSize: 14,
              fontFamily: "'IBM Plex Mono', monospace",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
        ))}

        {/* Error / success message */}
        {error && (
          <div style={{
            padding: "10px 12px",
            background: theme.errorBg,
            border: `1px solid ${theme.error}33`,
            borderRadius: 8,
            color: theme.error,
            fontSize: 13,
            marginBottom: 12,
          }}>
            {error}
          </div>
        )}


        {/* Submit */}
        <button
          onClick={handleSubmit}
          disabled={loading || !email || !password}
          style={{
            width: "100%",
            padding: "12px 0",
            background: `linear-gradient(135deg, ${theme.accent}, #00c98a)`,
            border: "none",
            borderRadius: 8,
            color: theme.bg,
            fontSize: 14,
            fontWeight: 700,
            fontFamily: "'IBM Plex Mono', monospace",
            cursor: loading || !email || !password ? "not-allowed" : "pointer",
            opacity: loading || !email || !password ? 0.5 : 1,
          }}
        >
          {loading ? "..." : mode === "signin" ? "Sign In" : "Sign Up"}
        </button>
        </>)}
      </div>
    </div>
  );
}
